import React, { ReactNode } from 'react'
import { Platform } from 'react-native'

import { Input, InputField } from '../input'
import { Pressable } from '../pressable'
import { ChevronDownGlyph, CloseGlyph } from '../primitive-glyphs/glyphs'

type InputRef = React.ComponentRef<typeof Input>

function assignRef(ref: React.Ref<unknown> | undefined, node: unknown) {
    if (typeof ref === 'function') {
        ref(node)
    } else if (ref) {
        ;(ref as React.MutableRefObject<unknown>).current = node
    }
}

interface DropdownTriggerProps {
    triggerProps: { ref?: React.Ref<unknown> }
    innerRef?: (node: InputRef | null) => void
    value: string
    valueNode?: ReactNode
    valueLabel?: string
    placeholder?: string
    disabled?: boolean
    isInvalid?: boolean
    isOpen: boolean
    showClear: boolean
    className?: string
    nativeID?: string
    testID?: string
    onFocus: () => void
    onChangeText?: (text: string) => void
    onClear: () => void
    onToggle: () => void
    onMeasureWidth: (width: number) => void
}

export function DropdownTrigger({
    triggerProps,
    innerRef,
    value,
    valueNode,
    valueLabel,
    placeholder,
    disabled,
    isInvalid,
    isOpen,
    showClear,
    className,
    nativeID,
    testID,
    onFocus,
    onChangeText,
    onClear,
    onToggle,
    onMeasureWidth,
}: DropdownTriggerProps) {
    const { ref: popoverRef } = triggerProps
    const lastWidthRef = React.useRef(0)
    const fieldRef = React.useRef<React.ComponentRef<typeof InputField> | null>(null)

    const shouldFocusField = Platform.OS === 'web' && isOpen && !valueNode
    React.useEffect(() => {
        if (shouldFocusField) {
            ;(fieldRef.current as { focus?: () => void } | null)?.focus?.()
        }
    }, [shouldFocusField])

    const measureRef = React.useCallback<React.RefCallback<InputRef>>(
        node => {
            assignRef(popoverRef, node)
            innerRef?.(node)
            const rect = (
                node as { getBoundingClientRect?: () => { width: number } } | null
            )?.getBoundingClientRect?.()
            const width = rect?.width ? Math.round(rect.width) : 0
            if (width && width !== lastWidthRef.current) {
                lastWidthRef.current = width
                onMeasureWidth(width)
            }
        },
        [popoverRef, innerRef, onMeasureWidth],
    )

    return (
        <Input ref={measureRef} isDisabled={disabled} isInvalid={isInvalid} className={className}>
            {valueNode ? (
                <Pressable
                    accessibilityRole='button'
                    accessibilityLabel={valueLabel}
                    accessibilityState={{ disabled, expanded: isOpen }}
                    disabled={disabled}
                    nativeID={nativeID}
                    testID={testID}
                    onPress={onToggle}
                    className='h-full min-w-0 flex-1 flex-row items-center'
                >
                    {valueNode}
                </Pressable>
            ) : (
                <InputField
                    ref={fieldRef}
                    value={value}
                    accessibilityLabel={valueLabel}
                    accessibilityValue={valueLabel ? { text: valueLabel } : undefined}
                    placeholder={placeholder}
                    editable={!disabled}
                    nativeID={nativeID}
                    testID={testID}
                    onFocus={onFocus}
                    onChangeText={onChangeText}
                />
            )}
            {showClear && (
                <Pressable
                    accessibilityRole='button'
                    onPress={onClear}
                    className='h-6 w-6 shrink-0 items-center justify-center'
                >
                    <CloseGlyph size={20} />
                </Pressable>
            )}
            <Pressable
                accessibilityRole='button'
                onPress={onToggle}
                className='h-6 w-6 shrink-0 items-center justify-center'
            >
                <ChevronDownGlyph size={24} />
            </Pressable>
        </Input>
    )
}
